import { useRef, useState, useCallback } from "react";
import { ZoomIn, X } from "lucide-react";

/**
 * Product image with:
 *  - Desktop: hover → 2× lens follows the cursor inside the image
 *  - Mobile/touch: small "Zoom" button → opens a fullscreen 2× modal with pan
 */
export default function ZoomableImage({ src, alt, className = "", zoom = 2, fit = "cover", children }) {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [modalOpen, setModalOpen] = useState(false);

  const onMove = useCallback((e) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  }, []);

  return (
    <>
      <div
        ref={wrapRef}
        className={`relative overflow-hidden ${className}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onMouseMove={onMove}
        data-testid="zoomable-image"
      >
        {/* Base image */}
        <img
          src={src}
          alt={alt}
          className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"} select-none pointer-events-none`}
          draggable={false}
        />

        {/* Desktop hover-zoom layer */}
        <div
          aria-hidden
          className="absolute inset-0 hidden md:block transition-opacity duration-200 pointer-events-none"
          style={{
            opacity: hover ? 1 : 0,
            backgroundImage: `url(${src})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${zoom * 100}%`,
            backgroundPosition: `${pos.x}% ${pos.y}%`,
          }}
        />

        {/* Cursor lens dot (desktop only) */}
        <div
          aria-hidden
          className="absolute hidden md:block w-24 h-24 rounded-full border border-[#FAFAF7]/80 shadow-[0_4px_16px_rgba(0,0,0,0.15)] transition-opacity duration-200 pointer-events-none"
          style={{
            opacity: hover ? 1 : 0,
            left: `calc(${pos.x}% - 48px)`,
            top: `calc(${pos.y}% - 48px)`,
            background: "radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)",
            mixBlendMode: "overlay",
          }}
        />

        {/* Decorative overlay (slot) */}
        {children}

        {/* Hover hint badge (desktop) — bottom-left so it doesn't clash with corner doodles */}
        <div
          aria-hidden
          className="absolute bottom-5 left-5 hidden md:flex items-center gap-1.5 rounded-full bg-[#FAFAF7]/92 backdrop-blur px-3 py-1.5 text-[11px] font-medium text-[#2C2C2A] tracking-[0.05em] transition-opacity duration-300"
          style={{ opacity: hover ? 0 : 0.85 }}
        >
          <ZoomIn className="w-3 h-3" /> Hover to zoom
        </div>

        {/* Mobile tap-to-zoom button */}
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="md:hidden absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-[#FAFAF7]/95 backdrop-blur px-3 py-1.5 text-[11px] font-medium text-[#2C2C2A] tracking-[0.05em] shadow"
          data-testid="zoom-mobile-trigger"
          aria-label="Zoom image"
        >
          <ZoomIn className="w-3 h-3" /> Zoom
        </button>
      </div>

      {/* Mobile zoom modal — fullscreen, 2× by default, pan with finger */}
      {modalOpen && (
        <div
          className="md:hidden fixed inset-0 z-[80] bg-[#2C2C2A]/95 backdrop-blur flex items-center justify-center"
          data-testid="zoom-modal"
          onClick={() => setModalOpen(false)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setModalOpen(false); }}
            className="absolute top-5 right-5 z-10 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FAFAF7]/95 text-[#2C2C2A]"
            aria-label="Close zoom"
            data-testid="zoom-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-full h-full overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={src}
              alt={alt}
              className="select-none"
              draggable={false}
              style={{ width: `${zoom * 100}%`, maxWidth: "none", height: "auto" }}
            />
          </div>
          <p className="absolute bottom-6 left-0 right-0 text-center text-[11px] text-[#FAFAF7]/80 uppercase tracking-[0.18em] pointer-events-none">
            Drag to explore — tap outside to close
          </p>
        </div>
      )}
    </>
  );
}
